'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/app/context/AuthContext';
import { useVersion } from '@/app/context/VersionContext';
import { createSupabaseBrowserClient } from '@/app/lib/supabase/client';

// --- Type definitions ---

interface SavedElementBase {
  id: number;
  user_id: string;
  type: string;
  display_name: string;
  thumbnail_url: string | null;
  created_at: string;
}

export interface SavedProfile extends SavedElementBase {
  type: 'profile';
  metadata: {
    key: string;       // UUID (dedup key)
    username: string;
    uuid: string;
    skinUrl: string | null;
  };
}

export interface SavedSkin extends SavedElementBase {
  type: 'skin';
  metadata: {
    key: string;       // content hash or name, or "skin-{uuid}" for player skins
    modelType?: 'classic' | 'slim';
    textureBase64?: string;
    // Player skin fields (from UUID lookup)
    skinUrl?: string;
    username?: string;
    uuid?: string;
  };
}

export interface SavedArmor extends SavedElementBase {
  type: 'armor';
  metadata: {
    key: string;       // dedup key from name
    stateHash: string; // base64-encoded ArmorBuilderState (same as URL hash format)
    version: string;   // game version used when saving
    summary: string;   // e.g. "Diamond Set + 8 enchantments"
  };
}

export interface SavedBanner extends SavedElementBase {
  type: 'banner';
  metadata: {
    key: string;       // dedup key from name
    stateHash: string; // URL hash format
    version: string;
    summary: string;
    mode: 'banner' | 'shield';
  };
}

export interface SavedPotion extends SavedElementBase {
  type: 'potion';
  metadata: {
    key: string;       // dedup key from name
    stateHash: string; // URL hash format
    version: string;
    summary: string;
  };
}

export interface SavedSign extends SavedElementBase {
  type: 'sign';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedBook extends SavedElementBase {
  type: 'book';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedFirework extends SavedElementBase {
  type: 'firework';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedEnchantment extends SavedElementBase {
  type: 'enchantment';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedArmorStand extends SavedElementBase {
  type: 'armor_stand';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedMobBuilder extends SavedElementBase {
  type: 'mob_builder';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedTellraw extends SavedElementBase {
  type: 'tellraw';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedCommand extends SavedElementBase {
  type: 'command';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedExecute extends SavedElementBase {
  type: 'execute';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedTitle extends SavedElementBase {
  type: 'title';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedShape extends SavedElementBase {
  type: 'pixel_shape';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedMotd extends SavedElementBase {
  type: 'motd';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedColorCode extends SavedElementBase {
  type: 'color_code';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export interface SavedLootTable extends SavedElementBase {
  type: 'loot_table';
  metadata: {
    key: string;
    stateHash: string;
    version: string;
    summary: string;
  };
}

export type SavedElement = SavedProfile | SavedSkin | SavedArmor | SavedBanner | SavedPotion | SavedSign | SavedBook | SavedFirework | SavedEnchantment | SavedArmorStand | SavedMobBuilder | SavedTellraw | SavedCommand | SavedExecute | SavedTitle | SavedShape | SavedMotd | SavedColorCode | SavedLootTable;
export type SavedElementType = SavedElement['type'];

/**
 * The dedupe key a name-keyed save gets. The unique index is
 * `(user_id, type, metadata->>'key')`, and `isSaved` / `getElementByKey` match on it, so an editor
 * recognizes its own saved build by this string and by nothing else.
 *
 * ⚠️ ONE definition, exported, because RENAMING has to reproduce it exactly. Seventeen helpers
 * inlined the same expression and rename did not touch the key at all, so a renamed build went
 * unrecognised by its own editor and saving it again inserted a SECOND row under the same visible
 * name. A rename that restated this derivation slightly differently would do it again.
 */
export function deriveSaveKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

// --- Params for saving ---

export interface SaveProfileParams {
  username: string;
  uuid: string;
  skinUrl: string | null;
}

export interface SaveSkinParams {
  name: string;
  modelType: 'classic' | 'slim';
  textureBase64: string;
}

export interface SavePlayerSkinParams {
  username: string;
  uuid: string;
  skinUrl: string;
}

export interface SaveArmorParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveBannerParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
  mode: 'banner' | 'shield';
}

export interface SavePotionParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveSignParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveBookParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveFireworkParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveEnchantmentParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveArmorStandParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveMobBuilderParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveTellrawParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveTitleParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveShapeParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveMotdParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveColorCodeParams {
  name: string;
  stateHash: string;
  summary: string;
}

export interface SaveLootTableParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveCommandParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

export interface SaveExecuteParams {
  name: string;
  stateHash: string;
  version: string;
  summary: string;
}

// --- Mod context helper (module scope, so it never becomes a hook dependency) ---

/** The `mods` fragment to merge into a builder save's metadata — present only when a modpack or
 *  individual mods are active, so vanilla-context saves stay byte-identical to before. */
function buildModsMeta(sel: { modpack: string | null; individualMods: string[] }): { mods?: { modpack: string | null; individualMods: string[] } } {
  return sel.modpack || sel.individualMods.length
    ? { mods: { modpack: sel.modpack, individualMods: [...sel.individualMods] } }
    : {};
}

// --- Hook ---

export function useSavedElements() {
  const { user } = useAuth();
  const [elements, setElements] = useState<SavedElement[]>([]);
  const [loading, setLoading] = useState(false);
  const savingKeys = useRef(new Set<string>());
  // A ref mirror of `elements`, so mutators can read the CURRENT list synchronously without adding
  // `elements` to every useCallback's deps (which would rebuild them on every fetch/edit). Reading
  // a value out of a setState updater's side-effect is NOT reliably synchronous — that skipped the
  // DB write in updateElement — so the ref is the correct source for a pre-write snapshot.
  const elementsRef = useRef(elements);
  useEffect(() => { elementsRef.current = elements; }, [elements]);

  // Capture the mod context a build is saved under, so it can reopen with the same mods/modpack
  // (VersionContext reads them from the open URL). Stored ONLY when a modpack or individual mods are
  // active — a vanilla-context save carries no `mods` key and reopens exactly as before, so no
  // vanilla design needlessly reloads a modpack. Read from a ref (stable) so no save* gains a dep.
  const { modSelection } = useVersion();
  const modSelRef = useRef(modSelection);
  useEffect(() => { modSelRef.current = modSelection; }, [modSelection]);
  // Per-element write queue. Each UPDATE replaces the whole JSONB `metadata` blob, so two writes to
  // the same id that overlap on the wire race — whichever RESPONSE lands last wins for every field,
  // silently dropping the other edit. Chaining a row's writes makes the second start only after the
  // first resolves (and after its optimistic result is in `elementsRef`), so blobs accumulate.
  const writeChains = useRef(new Map<number, Promise<unknown>>());

  // Fetch all saved elements on auth change
  useEffect(() => {
    if (!user) {
      setElements([]);
      return;
    }

    // Guard against a response for a PREVIOUS user landing after this effect has
    // re-run (logout, or switching accounts). Without it, an in-flight fetch for
    // the old user can resolve after the `!user` branch cleared state and
    // repopulate the UI with the previous account's saved elements.
    let cancelled = false;
    setLoading(true);
    const supabase = createSupabaseBrowserClient();
    // PAGED: a plain select is silently capped at 1000 rows, and the library now depends on the FULL
    // list (duplicate naming, folders and tags, "is this saved"), so a user past 1000 saves lost their
    // oldest builds and could collide with the unique index. `id` breaks created_at ties so no row is
    // skipped or repeated across a page boundary.
    const PAGE = 1000;
    (async () => {
      const all: SavedElement[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from('saved_elements')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, from + PAGE - 1);
        if (cancelled) return;
        // A failed page leaves the list as it was rather than showing a partial library as complete.
        if (error) { setLoading(false); return; }
        all.push(...((data as SavedElement[]) ?? []));
        if (!data || data.length < PAGE) break;
      }
      setElements(all);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const isSaved = useCallback(
    (type: SavedElementType, key: string) =>
      elements.some(e => e.type === type && e.metadata.key === key),
    [elements]
  );

  const getElementByKey = useCallback(
    (type: SavedElementType, key: string) =>
      elements.find(e => e.type === type && e.metadata.key === key) ?? null,
    [elements]
  );

  // Guard to prevent duplicate save requests from rapid clicks
  const guardedSave = useCallback(
    async (type: string, key: string, fn: () => Promise<{ error: string | null }>): Promise<{ error: string | null }> => {
      const guardKey = `${type}:${key}`;
      if (savingKeys.current.has(guardKey)) return { error: 'Save already in progress.' };
      savingKeys.current.add(guardKey);
      try {
        return await fn();
      } finally {
        savingKeys.current.delete(guardKey);
      }
    },
    []
  );

  const saveProfile = useCallback(
    async ({ username, uuid, skinUrl }: SaveProfileParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };

      return guardedSave('profile', uuid, async () => {
        const element = {
          user_id: user.id,
          type: 'profile' as const,
          display_name: username,
          thumbnail_url: skinUrl,
          metadata: {
            key: uuid,
            username,
            uuid,
            skinUrl,
          },
        };

        const optimistic: SavedProfile = {
          ...element,
          id: Date.now(),
          created_at: new Date().toISOString(),
        };
        setElements(prev => [optimistic, ...prev]);

        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase
          .from('saved_elements')
          .insert(element)
          .select()
          .single();

        if (error) {
          setElements(prev => prev.filter(e => e.id !== optimistic.id));
          if (error.code === '23505') {
            return { error: 'This profile is already saved.' };
          }
          return { error: error.message };
        }

        setElements(prev =>
          prev.map(e => (e.id === optimistic.id ? (data as SavedProfile) : e))
        );
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveSkin = useCallback(
    async ({ name, modelType, textureBase64 }: SaveSkinParams): Promise<{ error: string | null; id?: number }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      // The new row's id, for callers that go on to name this build (publishing to the community does).
      let savedId: number | undefined;
      const result = await guardedSave('skin', key, async () => {
        const element = {
          user_id: user.id,
          type: 'skin' as const,
          display_name: name,
          thumbnail_url: null,
          metadata: { key, modelType, textureBase64 },
        };
        const optimistic: SavedSkin = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) {
          setElements(prev => prev.filter(e => e.id !== optimistic.id));
          return { error: error.code === '23505' ? 'A skin with this name already exists.' : error.message };
        }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedSkin) : e)));
        savedId = (data as SavedSkin).id;
        return { error: null };
      });
      return result.error ? result : { error: null, id: savedId };
    },
    [user, guardedSave]
  );

  const savePlayerSkin = useCallback(
    async ({ username, uuid, skinUrl }: SavePlayerSkinParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = `skin-${uuid}`;
      return guardedSave('skin', key, async () => {
        const element = {
          user_id: user.id,
          type: 'skin' as const,
          display_name: `${username}'s Skin`,
          thumbnail_url: skinUrl,
          metadata: { key, skinUrl, username, uuid },
        };
        const optimistic: SavedSkin = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) {
          setElements(prev => prev.filter(e => e.id !== optimistic.id));
          return { error: error.code === '23505' ? 'This skin is already saved.' : error.message };
        }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedSkin) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveArmor = useCallback(
    async ({ name, stateHash, version, summary }: SaveArmorParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('armor', key, async () => {
        const element = { user_id: user.id, type: 'armor' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedArmor = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'An armor build with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedArmor) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  // Returns the new row's id too: publishing from the banner/shield editor saves first and then names
  // that saved build to /api/designs/publish. (The optimistic row's Date.now() id is never returned.)
  const saveBanner = useCallback(
    async ({ name, stateHash, version, summary, mode }: SaveBannerParams): Promise<{ error: string | null; id?: number }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      let savedId: number | undefined;
      const result = await guardedSave('banner', key, async () => {
        const element = { user_id: user.id, type: 'banner' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, mode, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedBanner = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A design with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedBanner) : e)));
        savedId = (data as SavedBanner).id;
        return { error: null };
      });
      return result.error ? result : { error: null, id: savedId };
    },
    [user, guardedSave]
  );

  const savePotion = useCallback(
    async ({ name, stateHash, version, summary }: SavePotionParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('potion', key, async () => {
        const element = { user_id: user.id, type: 'potion' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedPotion = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A potion with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedPotion) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveSign = useCallback(
    async ({ name, stateHash, version, summary }: SaveSignParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('sign', key, async () => {
        const element = { user_id: user.id, type: 'sign' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedSign = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A sign with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedSign) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveBook = useCallback(
    async ({ name, stateHash, version, summary }: SaveBookParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('book', key, async () => {
        const element = { user_id: user.id, type: 'book' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedBook = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A book with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedBook) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveFirework = useCallback(
    async ({ name, stateHash, version, summary }: SaveFireworkParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('firework', key, async () => {
        const element = { user_id: user.id, type: 'firework' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedFirework = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A firework with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedFirework) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveEnchantment = useCallback(
    async ({ name, stateHash, version, summary }: SaveEnchantmentParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('enchantment', key, async () => {
        const element = { user_id: user.id, type: 'enchantment' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedEnchantment = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'An enchantment build with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedEnchantment) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveArmorStand = useCallback(
    async ({ name, stateHash, version, summary }: SaveArmorStandParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('armor_stand', key, async () => {
        const element = { user_id: user.id, type: 'armor_stand' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedArmorStand = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'An armor stand with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedArmorStand) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveMobBuilder = useCallback(
    async ({ name, stateHash, version, summary }: SaveMobBuilderParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('mob_builder', key, async () => {
        const element = { user_id: user.id, type: 'mob_builder' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedMobBuilder = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A mob build with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedMobBuilder) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveTellraw = useCallback(
    async ({ name, stateHash, version, summary }: SaveTellrawParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('tellraw', key, async () => {
        const element = { user_id: user.id, type: 'tellraw' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedTellraw = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A tellraw with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedTellraw) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveCommand = useCallback(
    async ({ name, stateHash, version, summary }: SaveCommandParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('command', key, async () => {
        const element = { user_id: user.id, type: 'command' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedCommand = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A command with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedCommand) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveExecute = useCallback(
    async ({ name, stateHash, version, summary }: SaveExecuteParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('execute', key, async () => {
        const element = { user_id: user.id, type: 'execute' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedExecute = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'An execute command with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedExecute) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveTitle = useCallback(
    async ({ name, stateHash, version, summary }: SaveTitleParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('title', key, async () => {
        const element = { user_id: user.id, type: 'title' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedTitle = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A title with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedTitle) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveShape = useCallback(
    async ({ name, stateHash, version, summary }: SaveShapeParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('pixel_shape', key, async () => {
        const element = { user_id: user.id, type: 'pixel_shape' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedShape = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A shape with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedShape) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveMotd = useCallback(
    async ({ name, stateHash, version, summary }: SaveMotdParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('motd', key, async () => {
        const element = { user_id: user.id, type: 'motd' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedMotd = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A MOTD with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedMotd) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  // Colored Text and Gradient generators share this type; the hash says which page opens it.
  const saveColorCode = useCallback(
    async ({ name, stateHash, summary }: SaveColorCodeParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('color_code', key, async () => {
        const element = { user_id: user.id, type: 'color_code' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version: 'n/a', summary } };
        const optimistic: SavedColorCode = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A design with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedColorCode) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  const saveLootTable = useCallback(
    async ({ name, stateHash, version, summary }: SaveLootTableParams): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const key = deriveSaveKey(name);
      return guardedSave('loot_table', key, async () => {
        const element = { user_id: user.id, type: 'loot_table' as const, display_name: name, thumbnail_url: null, metadata: { key, stateHash, version, summary, ...buildModsMeta(modSelRef.current) } };
        const optimistic: SavedLootTable = { ...element, id: Date.now(), created_at: new Date().toISOString() };
        setElements(prev => [optimistic, ...prev]);
        const supabase = createSupabaseBrowserClient();
        const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
        if (error) { setElements(prev => prev.filter(e => e.id !== optimistic.id)); return { error: error.code === '23505' ? 'A loot table with this name already exists.' : error.message }; }
        setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedLootTable) : e)));
        return { error: null };
      });
    },
    [user, guardedSave]
  );

  // Update a saved element's name and/or metadata. Metadata is a JSONB column, so a partial
  // patch is shallow-MERGED into the existing object here and the whole object is written back —
  // there is no column-level partial update for JSONB. Organisational fields (folder, tags,
  // pinned) live inside metadata, which is why this is the single write path for them.
  //
  // ⚠️ RLS: an UPDATE blocked by row-level security affects 0 rows and returns NO error, so a
  // caller cannot infer success from `error === null` alone — the optimistic state is the source
  // of truth for the UI, and Phase-2 verification re-queries the row rather than trusting this.
  const updateElement = useCallback(
    async (
      id: number,
      patch: { display_name?: string; metadata?: Record<string, unknown> },
    ): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };

      // Read the pre-write snapshot from the ref — reliably synchronous, unlike a value captured
      // inside the setElements updater below.
      const prev = elementsRef.current.find(e => e.id === id);
      if (!prev) return { error: 'Element not found' };

      const nextName = patch.display_name ?? prev.display_name;
      const nextMeta = patch.metadata
        ? { ...(prev.metadata as Record<string, unknown>), ...patch.metadata }
        : (prev.metadata as Record<string, unknown>);
      const nextEl = { ...prev, display_name: nextName, metadata: nextMeta } as SavedElement;

      // Keep the ref's copy of THIS element fresh SYNCHRONOUSLY. Because the DB write replaces the
      // whole JSONB `metadata` blob, a second edit to the same id fired before React commits would
      // otherwise re-read the pre-edit base and clobber the first field. Updating the ref here means
      // a rapid follow-up merges onto the already-updated metadata (the DB payload accumulates).
      elementsRef.current = elementsRef.current.map(e => (e.id === id ? nextEl : e));
      setElements(list => list.map(e => (e.id === id ? nextEl : e)));

      const dbPatch: Record<string, unknown> = {};
      if (patch.display_name !== undefined) dbPatch.display_name = nextName;
      if (patch.metadata) dbPatch.metadata = nextMeta;

      const supabase = createSupabaseBrowserClient();
      // Serialize this row's writes: start only after the previous one for the same id resolves, so
      // two overlapping edits can't race to last-write-wins on the whole blob.
      const prior = writeChains.current.get(id) ?? Promise.resolve();
      const chained = prior
        .catch(() => {})
        .then(() => supabase.from('saved_elements').update(dbPatch).eq('id', id).eq('user_id', user.id));
      writeChains.current.set(id, chained);
      const { error } = (await chained) as { error: { message: string } | null };
      if (writeChains.current.get(id) === chained) writeChains.current.delete(id);

      if (error) {
        // Roll back ONLY when nothing newer is queued for this row. A later edit computed its
        // payload from the already-updated ref, so it carries this write's fields too: restoring
        // `prev` here would wipe the newer edit from the UI while the DB ends up holding both.
        // Leave the display to the queued write, which reports its own failure if it fails.
        const newerQueued = writeChains.current.get(id) !== undefined;
        if (!newerQueued) {
          elementsRef.current = elementsRef.current.map(e => (e.id === id ? prev : e));
          setElements(list => list.map(e => (e.id === id ? prev : e)));
        }
        return { error: error.message };
      }
      return { error: null };
    },
    [user],
  );

  // Duplicate an existing saved element. The unique index is (user_id, type, metadata->>'key'),
  // so the copy MUST get a fresh key and name or the insert violates the constraint. Generic over
  // every builder type — it copies the whole metadata blob and only rewrites key/name.
  const duplicateElement = useCallback(
    async (id: number): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      const src = elementsRef.current.find(e => e.id === id);
      if (!src) return { error: 'Element not found' };

      // ⚠️ The key MUST be deriveSaveKey(name), never a separately-invented string.
      // This used to mint `<oldkey>-copy-<timestamp>` while naming the row "X (copy)", so the
      // two disagreed: deriveSaveKey("X (copy)") is "x-(copy)". Re-saving the copy from an editor
      // under the name already shown in the box therefore matched NO existing row, the unique
      // index (user_id, type, metadata->>'key') never fired, and the insert succeeded — leaving
      // TWO rows under one name holding different designs. That is the same defect renaming had.
      // So: disambiguate the NAME, then derive the key from it, and the constraint does the rest.
      // Both the derived key AND the stored `metadata.key`: the unique index is on the stored key,
      // and a row saved before the rename fix can carry a key that no longer matches its name.
      // Checking names alone let such a row collide on every retry, so "try again" never worked.
      const taken = new Set(
        elementsRef.current
          .filter(e => e.type === src.type)
          .flatMap(e => [deriveSaveKey(e.display_name), (e.metadata as { key?: unknown } | null)?.key])
          .filter((k): k is string => typeof k === 'string' && k.length > 0),
      );
      let name = `${src.display_name} (copy)`;
      for (let n = 2; taken.has(deriveSaveKey(name)); n += 1) name = `${src.display_name} (copy ${n})`;
      const meta = { ...(src.metadata as Record<string, unknown>), key: deriveSaveKey(name) };
      const element = { user_id: user.id, type: src.type, display_name: name, thumbnail_url: src.thumbnail_url, metadata: meta };

      const optimistic = { ...element, id: Date.now(), created_at: new Date().toISOString() } as unknown as SavedElement;
      setElements(prev => [optimistic, ...prev]);

      const supabase = createSupabaseBrowserClient();
      const { data, error } = await supabase.from('saved_elements').insert(element).select().single();
      if (error) {
        setElements(prev => prev.filter(e => e.id !== optimistic.id));
        // The name disambiguation above reads the LOCAL list, so a second tab (or a double-click
        // racing itself) can still lose to the unique index. Say that in English — the raw
        // Postgres text names a constraint the user has no way to interpret.
        const code = (error as { code?: string }).code;
        return { error: code === '23505' ? 'A design with that name already exists — try again.' : error.message };
      }
      setElements(prev => prev.map(e => (e.id === optimistic.id ? (data as SavedElement) : e)));
      return { error: null };
    },
    [user],
  );

  const removeElement = useCallback(
    async (id: number) => {
      if (!user) return;

      // Optimistic remove — capture removed element via functional update
      let removed: SavedElement | undefined;
      setElements(prev => {
        removed = prev.find(e => e.id === id);
        return prev.filter(e => e.id !== id);
      });

      const supabase = createSupabaseBrowserClient();
      const { error } = await supabase
        .from('saved_elements')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error && removed) {
        // Rollback
        setElements(prev => [...prev, removed!]);
      }
    },
    [user]
  );

  const removeSavedByKey = useCallback(
    async (type: SavedElementType, key: string) => {
      const el = elements.find(e => e.type === type && e.metadata.key === key);
      if (el) await removeElement(el.id);
    },
    [elements, removeElement]
  );

  return {
    elements,
    loading,
    isSaved,
    getElementByKey,
    saveProfile,
    saveSkin,
    savePlayerSkin,
    saveArmor,
    saveBanner,
    savePotion,
    saveSign,
    saveBook,
    saveFirework,
    saveEnchantment,
    saveArmorStand,
    saveMobBuilder,
    saveTellraw,
    saveCommand,
    saveExecute,
    saveTitle,
    saveShape,
    saveMotd,
    saveColorCode,
    saveLootTable,
    updateElement,
    duplicateElement,
    removeElement,
    removeSavedByKey,
  };
}
